import { useState, useMemo } from "react";
import { MapPin, Search } from "lucide-react";
import { LOCATIONS, BRAND_FILTERS, CITIES, BRANDS, IMAGES, LOGOS } from "@/data/site";
import Reveal from "@/components/Reveal";

const FORMATS = ["All Formats", "Dine-in", "Cafe", "QSR", "Kiosk"];

const BRAND_IMG = {
  ProMill: IMAGES.promillBowl,
  GoMill: IMAGES.gomillTruck,
  "Millet Plaza": IMAGES.milletPlazaDining,
  Stories: IMAGES.storiesInterior,
};
const BRAND_LOGO = {
  ProMill: LOGOS.promill,
  GoMill: LOGOS.gomill,
  "Millet Plaza": LOGOS.millet_plaza,
  Stories: LOGOS.stories,
};

const FORMAT_ICON = {
  "Dine-in": "Dine-in",
  Cafe: "Café",
  QSR: "Takeaway",
  Kiosk: "Takeaway",
};

export default function StoreLocator() {
  const [brand, setBrand] = useState("All Brands");
  const [city, setCity] = useState("Pune");
  const [format, setFormat] = useState("All Formats");

  const list = useMemo(() => {
    return LOCATIONS.filter(
      (l) =>
        (brand === "All Brands" || l.brand === brand) &&
        (city === "All Cities" || l.city === city) &&
        (format === "All Formats" || l.format === format)
    );
  }, [brand, city, format]);

  const mapQuery = city === "All Cities" ? "India" : `${city}, India`;

  return (
    <div data-testid="page-locator" className="bg-[var(--bg)]">
      {/* HERO */}
      <section className="pt-[110px] md:pt-[120px] pb-10 bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <Reveal className="lg:col-span-4">
            <div className="overline overline-green">Our Ecosystem. Everywhere.</div>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight text-[var(--ink)] font-semibold">
              Find a Native Touch <br />
              <span className="text-[var(--gold)]">Brand Near You.</span>
            </h1>
            <div className="h-[3px] w-20 bg-[var(--gold)] mt-6 rounded-full" />
            <p className="mt-6 text-[var(--ink-2)] leading-relaxed">
              From everyday meals to café moments — explore our four brands across dine-in,
              on-the-go and retail formats.
            </p>

            {/* Brand chips */}
            <div className="mt-7 grid grid-cols-4 gap-2">
              {BRANDS.map((b) => (
                <button
                  key={b.slug}
                  data-testid={`brand-chip-${b.slug}`}
                  onClick={() => setBrand(brand === b.name.split("—")[0].trim() ? "All Brands" : b.name.split("—")[0].trim())}
                  className={`p-2 rounded-md border transition flex items-center justify-center min-h-[64px] ${
                    brand === b.name.split("—")[0].trim()
                      ? "border-[var(--green)] bg-[var(--green)] shadow-md"
                      : "border-[var(--line)] bg-white hover:border-[var(--green)]"
                  }`}
                >
                  <img src={b.logo} alt={b.name} className="max-h-9 w-auto object-contain" />
                </button>
              ))}
            </div>
          </Reveal>

          {/* Map */}
          <Reveal delay={0.1} className="lg:col-span-8">
            <div className="aspect-[4/3] md:aspect-[16/10] rounded-2xl overflow-hidden border border-[var(--line)] bg-[var(--bg-soft)]">
              <iframe
                data-testid="locator-map"
                title="Native Touch locations"
                width="100%"
                height="100%"
                loading="lazy"
                src={`https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`}
                className="border-0 w-full h-full"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="py-6 bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="card-soft p-3 md:p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-stretch">
            <FilterCell label="Select Brand" testId="locator-filter-brand" value={brand} setValue={setBrand} options={BRAND_FILTERS} />
            <FilterCell label="City" testId="locator-filter-city" value={city} setValue={setCity} options={CITIES} />
            <FilterCell label="Format" testId="locator-filter-format" value={format} setValue={setFormat} options={FORMATS} />
            <div className="flex flex-col justify-center">
              <button
                onClick={() => { setBrand("All Brands"); setCity("Pune"); setFormat("All Formats"); }}
                data-testid="locator-clear"
                className="text-xs text-[var(--green)] font-semibold underline mt-5"
              >
                Clear All
              </button>
            </div>
            <button data-testid="locator-view-results" className="btn-primary !py-3">
              <Search size={14} /> View {list.length} Locations
            </button>
          </div>
        </div>
      </section>

      {/* LOCATION LIST */}
      <section className="pb-20">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif-display text-2xl md:text-3xl font-semibold text-[var(--ink)]">
              {list.length} Location{list.length !== 1 ? "s" : ""} {city !== "All Cities" ? `in ${city}` : "across India"}
            </h2>
            <span className="kbd text-[var(--ink-3)] hidden md:inline">Sorted by: Nearest</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {list.map((l, i) => (
              <Reveal key={l.id} delay={(i % 4) * 0.04}>
                <div data-testid={`location-row-${l.id}`} className="card-soft overflow-hidden h-full flex flex-col">
                  <div className="relative aspect-[5/4] overflow-hidden bg-[var(--bg-soft)]">
                    <img src={BRAND_IMG[l.brand]} alt={l.brand} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 bg-white p-1.5 rounded-md shadow-sm">
                      <img src={BRAND_LOGO[l.brand]} alt={l.brand} className="h-7 w-7 object-contain" />
                    </div>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-serif-display text-lg font-semibold text-[var(--ink)] leading-tight">{l.brand} — {l.city}</h3>
                    <div className="kbd text-[var(--green)] mt-1">{FORMAT_ICON[l.format] || l.format}</div>
                    <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed flex items-start gap-2">
                      <MapPin size={14} className="text-[var(--ink-3)] mt-0.5 shrink-0" />
                      {l.address}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {(l.format === "Dine-in" ? ["Dine-in", "Takeaway", "Delivery"] : l.format === "Cafe" ? ["Dine-in", "Takeaway"] : ["Takeaway", "Delivery"]).map((tag) => (
                        <span key={tag} className="text-[10px] uppercase tracking-widest border border-[var(--line-soft)] text-[var(--ink-3)] px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="mt-auto pt-5 flex items-center justify-between">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(l.address + ", " + l.city)}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`location-directions-${l.id}`}
                        className="text-xs uppercase tracking-widest text-[var(--green)] font-semibold link-underline"
                      >
                        View Details →
                      </a>
                      <span className="kbd text-[var(--ink-3)]">{l.distance}</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
            {!list.length && (
              <div className="col-span-full card-soft p-14 text-center">
                <p className="font-serif-display text-2xl text-[var(--green)]">No outlets match these filters.</p>
                <p className="text-sm text-[var(--ink-3)] mt-2">Try a different brand, city or format.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ONE GROUP. FOUR BRANDS. */}
      <section className="section bg-[var(--bg-soft)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <h3 className="font-serif-display text-3xl md:text-4xl font-semibold text-[var(--green)]">
              One Group. Four Brands. <span className="text-[var(--gold)]">Endless Possibilities.</span>
            </h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {[
              { t: "Dine-In Restaurants", s: "Nutritious millet meals crafted for everyday dining.", bg: "bg-[var(--green)] text-white" },
              { t: "On-the-Go Kiosks", s: "Quick, wholesome meals for your busy lifestyle.", bg: "bg-[var(--green)] text-white" },
              { t: "Retail Stores", s: "Goodness of millets for your home.", bg: "bg-[var(--green)] text-white" },
              { t: "Millet Cafés", s: "Warm spaces for conversations, coffee and comfort.", bg: "bg-[#f4e9d4] text-[var(--ink)]" },
            ].map((c) => (
              <div key={c.t} className={`rounded-2xl p-6 md:p-7 ${c.bg}`}>
                <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center mb-5">
                  <MapPin size={22} className={c.bg.includes("green") ? "text-white" : "text-[var(--green)]"} />
                </div>
                <h4 className="font-serif-display text-xl font-semibold leading-tight">{c.t}</h4>
                <p className={`text-sm mt-3 leading-relaxed ${c.bg.includes("green") ? "text-white/85" : "text-[var(--ink-2)]"}`}>{c.s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function FilterCell({ label, value, setValue, options, testId }) {
  return (
    <div className="flex flex-col">
      <label className="kbd text-[var(--ink-3)] mb-1.5">{label}</label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="bg-white border border-[var(--line)] rounded-md px-3 py-2.5 text-sm focus:border-[var(--green)] focus:outline-none"
      >
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}
